import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';

import { useGetProducts } from '~/entities/Requests/api/workwise/useGetProducts';
import { PagesPathsKeys } from '~/shared/constants/pages';
import { resolveEnabledRequestProducts } from '~/shared/constants/requestProducts';
import type { RouteObject } from '~/shared/contexts';
import {
  useAbility,
  useFlags,
  useSessionInfo,
  useUserProfile
} from '~/shared/contexts';
import { isAdministrator, UserProfileType } from '~/shared/types/profile';

import {
  buildRequestProductSubRoutes,
  getSubRouteTitle,
  MENU_ROUTES_CONFIG,
  type SubRouteConfig
} from './menuRoutesConfig';

type AppVariant = 'app' | 'eib';

interface UseMenuRoutesOptions {
  variant: AppVariant;
}

export function useMenuRoutes({ variant }: UseMenuRoutesOptions) {
  const { t } = useTranslate();
  const ability = useAbility();
  const flags = useFlags();
  const { sessionInfo } = useSessionInfo();
  const { data: apiProducts } = useGetProducts();
  const enabledRequestProducts = useMemo(
    () => resolveEnabledRequestProducts(apiProducts),
    [apiProducts]
  );

  const userProfileResult = useUserProfile();
  const {
    isLoading: profileLoading,
    profile: profileData,
    isEibZoneSpocUser
  } = userProfileResult;
  const isProxying = !!sessionInfo?.proxiedAs;

  const hasAuthorizedFlags = useCallback(
    (authorizedFlags: PagesPathsKeys | PagesPathsKeys[]) => {
      if (typeof authorizedFlags === 'string') {
        return flags[authorizedFlags as PagesPathsKeys]?.active ?? true;
      }
      if (Array.isArray(authorizedFlags)) {
        return authorizedFlags.some(
          flag => flags[flag as PagesPathsKeys]?.active ?? true
        );
      }
      return false;
    },
    [flags]
  );

  const hasRequiredProfile = useCallback(
    (profileRestriction?: UserProfileType[]) => {
      if (isProxying || isEibZoneSpocUser || isAdministrator(profileData)) {
        return true;
      }
      if (!profileRestriction) {
        return true;
      }
      if (profileLoading) {
        return true;
      }
      return profileData?.profileType
        ? profileRestriction.includes(profileData.profileType)
        : false;
    },
    [profileData, profileLoading, isProxying, isEibZoneSpocUser]
  );

  const { menuRoutes, footerRoutes } = useMemo(() => {
    const menuRoutesList: RouteObject[] = [];
    const footerRoutesList: RouteObject[] = [];
    const prefix = variant === 'eib' ? '/eib' : '/myaccess';

    const processSubRoutes = (
      subRoutes: SubRouteConfig[],
      parentKey: string
    ): RouteObject[] => {
      return subRoutes
        .filter(subRoute => {
          const subHasFlagAccess = hasAuthorizedFlags(subRoute.flag);
          const subHasEntityAccess = ability.can('view', subRoute.entity);
          return subHasFlagAccess && subHasEntityAccess;
        })
        .map(subRoute => {
          const routeObject: RouteObject = {
            key: subRoute.key,
            title: getSubRouteTitle(subRoute, parentKey, t),
            path: `${prefix}/${subRoute.path}`,
            icon: subRoute.icon
          };

          if (subRoute.subRoutes?.eib && variant === 'eib') {
            routeObject.routeComponents = processSubRoutes(
              subRoute.subRoutes.eib,
              `${parentKey}.subRoutes.${subRoute.key}`
            );
          }

          return routeObject;
        });
    };

    MENU_ROUTES_CONFIG.forEach(route => {
      const hasFlagAccess = hasAuthorizedFlags(route.flag);
      const hasEntityAccess =
        ability.can('view', route.entity[variant]) || variant === 'eib';
      const hasProfileAccess = hasRequiredProfile(
        route.profileRestriction?.[variant]
      );

      if (hasFlagAccess && hasEntityAccess && hasProfileAccess) {
        const routePath = route.path[variant];
        const translatedTitle = t(
          `menu.${route.key[variant]}`,
          route.title[variant]
        );

        const routeObject: RouteObject = {
          key: `${prefix}/${route.key[variant]}`,
          icon: route.icons[variant],
          path: `${prefix}/${routePath}`,
          title: translatedTitle
        };

        const variantSubRoutes =
          variant === 'eib'
            ? route.subRoutes?.eib
            : route.key.app === 'newRequest'
            ? buildRequestProductSubRoutes(enabledRequestProducts)
            : route.subRoutes?.app;
        if (variantSubRoutes?.length) {
          routeObject.routeComponents = processSubRoutes(
            variantSubRoutes,
            route.key[variant] as string
          );
        }

        menuRoutesList.push(routeObject);
      }
    });

    return { menuRoutes: menuRoutesList, footerRoutes: footerRoutesList };
  }, [
    ability,
    enabledRequestProducts,
    hasAuthorizedFlags,
    hasRequiredProfile,
    variant,
    t
  ]);

  return { menuRoutes, footerRoutes };
}
